import first from "lodash/first";
import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import Placeholder from "scmp-app/components/newsletter/newsletter-category-item-slideshow/placeholder.svg";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import { useNewsletterLandingContext } from "scmp-app/pages/newsletters/contexts";
import type { mynewsNewsletterItemNewsletter$key } from "scmp-app/queries/__generated__/mynewsNewsletterItemNewsletter.graphql";

import {
  Container,
  Content,
  ContentHeader,
  Description,
  Footer,
  LongDescription,
  Name,
  PlaceholderContainer,
  Preview,
  SetUpNowButton,
  StyledBaseImage,
  Summary,
} from "./styles";

type Props = {
  className?: string;
  reference: mynewsNewsletterItemNewsletter$key;
};

export const MyNewsNewsletterItem: FunctionComponent<Props> = ({ className, reference }) => {
  const newsletter = useFragment(
    graphql`
      fragment mynewsNewsletterItemNewsletter on Newsletter {
        name
        images {
          title
          url
        }
        summary {
          text
        }
        description {
          text
        }
        longDescription {
          text
        }
        ...entityLink
      }
    `,
    reference,
  );

  const { isEmbed } = useNewsletterLandingContext();

  const handlePreviewClick = useCallback(() => {
    sendGA4Tracking({
      action: "click",
      category: "nl",
      customized_parameters: {
        newsletter_name: "mynews",
      },
      subcategory: "preview",
    });
  }, []);

  const renderImage = useCallback(() => {
    const newsletterImage = first(newsletter.images ?? []);

    if (notEmpty(newsletterImage?.url)) {
      return (
        <StyledBaseImage
          $aspectRatio="3/2"
          $objectFit="cover"
          alt={newsletterImage?.title ?? newsletter.name ?? ""}
          src={newsletterImage.url}
        />
      );
    }
    return (
      <PlaceholderContainer>
        <Placeholder />
      </PlaceholderContainer>
    );
  }, [newsletter.images, newsletter.name]);

  return (
    <Container className={className}>
      {renderImage()}

      <Content>
        <ContentHeader>
          {newsletter.summary?.text && <Summary>{newsletter.summary?.text}</Summary>}
        </ContentHeader>
        {newsletter.name && <Name>{newsletter.name}</Name>}
        {newsletter.description?.text && <Description>{newsletter.description?.text}</Description>}
      </Content>
      <Footer>
        {newsletter.longDescription?.text && (
          <LongDescription>{newsletter.longDescription?.text}</LongDescription>
        )}
        <SetUpNowButton
          anchorProps={{ "aria-label": "my news" }}
          as="/mynews"
          pathname="mynews"
          target={isEmbed ? "_blank" : undefined}
        >
          Set Up Now
        </SetUpNowButton>
        <Preview
          onClick={handlePreviewClick}
          reference={newsletter}
          shallow
          target={isEmbed ? "_blank" : undefined}
        >
          Preview
        </Preview>
      </Footer>
    </Container>
  );
};

MyNewsNewsletterItem.displayName = "MyNewsNewsletterItem";
